// Validation utilities for roll forming calculator

// Minimum lip gap is 3mm (or 0 for no lip)
export const MIN_LIP_GAP = 3;

// ===== THICKNESS VALIDATION FUNCTIONS =====

// Snap thickness to valid 0.1 increments within bounds
export const snapToValidThickness = (value) => {
  const t = parseFloat(value);
  if (isNaN(t)) return value;
  
  // Round to nearest 0.1
  const rounded = Math.round(t * 10) / 10;
  // Keep within bounds (0.8 to 2.0)
  const bounded = Math.max(0.8, Math.min(2.0, rounded));
  return bounded.toFixed(1);
};

// Get next valid thickness value (for increment/decrement)
export const getNextValidThickness = (currentValue, direction) => {
  const current = parseFloat(currentValue);
  if (isNaN(current)) return currentValue;
  
  // Step by 0.1
  const newValue = current + (direction * 0.1);
  // Round to avoid floating point issues
  const rounded = Math.round(newValue * 10) / 10;
  // Keep within bounds
  const bounded = Math.max(0.8, Math.min(2.0, rounded));
  return bounded.toFixed(1);
};

// ===== SIDE VALIDATION FUNCTIONS =====

// Snap a side value to the nearest valid value that results in 0.5mm gap increment
export const snapToValidSide = (value, thicknessVal) => {
  const side = parseFloat(value);
  const t = parseFloat(thicknessVal);
  if (isNaN(side) || isNaN(t)) return value;
  
  const setupDistance = side - (2 * t);
  // Round to nearest 0.5
  const roundedSetup = Math.round(setupDistance * 2) / 2;
  const validSide = roundedSetup + (2 * t);
  
  // Keep within reasonable bounds
  const bounded = Math.max(5, Math.min(60, validSide));
  return bounded.toFixed(1);
};

// Check if a side value results in a valid setup distance (0.5 increments)
export const isValidSide = (sideValue, thicknessVal) => {
  const side = parseFloat(sideValue);
  const t = parseFloat(thicknessVal);
  if (isNaN(side) || isNaN(t)) return true;
  
  const setupDistance = side - (2 * t);
  const doubled = setupDistance * 2;
  return Math.abs(doubled - Math.round(doubled)) < 0.001;
};

// Validate setup distance for error display
export const validateSetupDistance = (sideValue, thicknessValue, name) => {
  const side = parseFloat(sideValue);
  const t = parseFloat(thicknessValue);
  if (isNaN(side) || isNaN(t)) return null;
  
  if (!isValidSide(sideValue, thicknessValue)) {
    const snapped = snapToValidSide(sideValue, t);
    return `${name} (${sideValue}) results in invalid setup distance. Use ${snapped}`;
  }
  return null;
};

// Get next valid side value (for increment/decrement)
export const getNextValidSide = (currentValue, thicknessVal, direction) => {
  const current = parseFloat(currentValue);
  const t = parseFloat(thicknessVal);
  if (isNaN(current) || isNaN(t)) return currentValue;
  
  const currentSetup = current - (2 * t);
  
  let newSetup;
  if (direction > 0) {
    // Going up: find next 0.5 increment above current
    newSetup = Math.floor(currentSetup * 2 + 1) / 2;
  } else {
    // Going down: find next 0.5 increment below current
    newSetup = Math.ceil(currentSetup * 2 - 1) / 2;
  }
  
  const newSide = newSetup + (2 * t);
  
  // Keep within bounds
  const bounded = Math.max(5, Math.min(60, newSide));
  return bounded.toFixed(1);
};

// ===== LIP VALIDATION FUNCTIONS =====

// Helper to calculate lip gap with floating point protection
export const calcLipGap = (lip, thickness) => {
  // Round to 1 decimal place to avoid floating point issues like 6.6 - 3.6 = 2.9999999996
  return Math.round((lip - (2 * thickness)) * 10) / 10;
};

// Largest valid lip that fits within maxLip (base/2): gap must be a 0.5
// increment, so never clamp to an in-between unsupported value.
const maxValidLip = (t, maxLip) => {
  const maxGap = Math.floor(Math.round((maxLip - 2 * t) * 2 * 1000) / 1000) / 2;
  if (maxGap < MIN_LIP_GAP) return 0; // no room for any lip
  return maxGap + 2 * t;
};

// Snap a lip value to the nearest valid value that results in valid lip gap
export const snapToValidLip = (value, thicknessVal, baseVal) => {
  const lip = parseFloat(value);
  const t = parseFloat(thicknessVal);
  const maxLip = parseFloat(baseVal) / 2;
  
  if (isNaN(lip) || isNaN(t)) return value;
  if (lip === 0) return '0'; // 0 is always valid (no lip)
  
  const lipGap = calcLipGap(lip, t);
  
  // If gap would be less than minimum, snap to minimum valid lip
  if (lipGap < MIN_LIP_GAP) {
    const minLip = MIN_LIP_GAP + (2 * t);
    if (minLip > maxLip) return '0';
    return minLip.toFixed(1);
  }
  
  // Round lip gap to nearest 0.5
  const roundedGap = Math.round(lipGap * 2) / 2;
  // Ensure it's at least MIN_LIP_GAP
  const validGap = Math.max(MIN_LIP_GAP, roundedGap);
  const validLip = validGap + (2 * t);
  
  // Cap at the largest VALID lip within bounds (0 to half of base)
  const bounded = Math.max(0, Math.min(maxValidLip(t, maxLip), validLip));
  return bounded.toFixed(1);
};

// Check if a lip value results in a valid lip gap
export const isValidLip = (lipValue, thicknessVal) => {
  const lip = parseFloat(lipValue);
  const t = parseFloat(thicknessVal);
  
  if (isNaN(lip) || isNaN(t)) return true;
  if (lip === 0) return true; // 0 is valid (no lip)
  
  const lipGap = calcLipGap(lip, t);
  
  // Check minimum gap (use small epsilon for floating point)
  if (lipGap < MIN_LIP_GAP - 0.01) return false;
  
  // Check 0.5 increment
  const doubled = lipGap * 2;
  return Math.abs(doubled - Math.round(doubled)) < 0.01;
};

// Validate lip gap for error display
export const validateLipGap = (lipValue, thicknessValue, name, baseValue) => {
  const lip = parseFloat(lipValue);
  const t = parseFloat(thicknessValue);
  
  if (isNaN(lip) || isNaN(t)) return null;
  if (lip === 0) return null; // 0 is valid
  
  if (!isValidLip(lipValue, thicknessValue)) {
    const snapped = snapToValidLip(lipValue, t, baseValue);
    const lipGap = calcLipGap(lip, t);
    if (lipGap < MIN_LIP_GAP - 0.01) {
      return `${name} (${lipValue}) results in gap < ${MIN_LIP_GAP}mm. Use ${snapped} or 0`;
    }
    return `${name} (${lipValue}) results in invalid gap. Use ${snapped}`;
  }
  return null;
};

// Get next valid lip value (for increment/decrement)
export const getNextValidLip = (currentValue, thicknessVal, baseVal, direction) => {
  const current = parseFloat(currentValue);
  const t = parseFloat(thicknessVal);
  const maxLip = parseFloat(baseVal) / 2;
  
  if (isNaN(current) || isNaN(t)) return currentValue;
  
  // Special case: going down from a small value should go to 0
  if (direction < 0) {
    const currentGap = calcLipGap(current, t);
    // Use small epsilon for floating point comparison
    if (currentGap <= MIN_LIP_GAP + 0.01) {
      return '0';
    }
  }
  
  // Special case: going up from 0 should go to minimum valid lip
  if (direction > 0 && current === 0) {
    const minLip = MIN_LIP_GAP + (2 * t);
    if (minLip > maxLip) return '0'; // base too small for any lip
    return minLip.toFixed(1);
  }
  
  const currentGap = calcLipGap(current, t);
  
  let newGap;
  if (direction > 0) {
    // Going up: find next 0.5 increment above current (at least MIN_LIP_GAP)
    // Round to avoid floating point issues
    const doubled = Math.round((currentGap * 2 + 1) * 1000) / 1000;
    newGap = Math.max(MIN_LIP_GAP, Math.floor(doubled) / 2);
    // Stop at the largest VALID gap that fits within base/2 —
    // never step onto an unsupported in-between value.
    const maxGap = Math.floor(Math.round((maxLip - 2 * t) * 2 * 1000) / 1000) / 2;
    if (newGap > maxGap) {
      if (maxGap < MIN_LIP_GAP) return current.toFixed(1);
      newGap = maxGap;
    }
  } else {
    // Going down: find next 0.5 increment below current
    // Round to avoid floating point issues (e.g., 6.000000000000001 should be 6)
    const doubled = Math.round((currentGap * 2 - 1) * 1000) / 1000;
    newGap = Math.ceil(doubled) / 2;
    // If we'd go below minimum, go to 0
    if (newGap < MIN_LIP_GAP) {
      return '0';
    }
  }
  
  const newLip = newGap + (2 * t);
  
  // Keep within bounds (newGap is already capped at the largest valid gap)
  const bounded = Math.max(0, newLip);
  return bounded.toFixed(1);
};

// ===== SPACER CALCULATION =====

// Sides smaller than this (mm) cannot be folded on the side-rolling stations.
// Instead they get folded together with the lip on stations 2-4.
export const SHORT_SIDE_THRESHOLD = 15;

// Round thickness for spacer calculation
export const roundThicknessForSpacer = (thickness) => {
  const t = parseFloat(thickness);
  if (t >= 0.8 && t <= 1.0) return 1.0;
  if (t > 1.0 && t <= 1.5) return 1.5;
  if (t > 1.5 && t <= 2.0) return 2.0;
  return t;
};

// Calculate all spacer values
export const calculateSpacers = (base, leftSide, rightSide, leftLip, rightLip, thickness, requiredSheet) => {
  const baseNum = parseFloat(base);
  const leftNum = parseFloat(leftSide);
  const rightNum = parseFloat(rightSide);
  const leftLipNum = parseFloat(leftLip) || 0;
  const rightLipNum = parseFloat(rightLip) || 0;
  const thicknessNum = parseFloat(thickness);
  
  const roundedThickness = roundThicknessForSpacer(thicknessNum);
  
  // Short side detection: sides < 15mm bypass side rolling and fold at lip rolling
  const leftIsShort = leftNum < SHORT_SIDE_THRESHOLD;
  const rightIsShort = rightNum < SHORT_SIDE_THRESHOLD;
  
  // Count angles for the profile (unchanged - physical profile geometry is the same)
  let numberOfAngles = 2; // Base U-shape has 2 angles
  if (leftLipNum > 0) numberOfAngles += 1;
  if (rightLipNum > 0) numberOfAngles += 1;
  
  // Calculate spacers for stations 5-10 (side rolling)
  // Bottom/top spacer widths only depend on base, regardless of short sides
  const bottomSpacer_5_10 = baseNum;
  const topSpacer_5_10 = baseNum - (2 * roundedThickness);
  
  // Setup distance (gap left to side-rolling rollers) = 0 if side is short (skipped)
  const setupDistanceLeft = leftIsShort ? 0 : leftNum - (2 * thicknessNum);
  const setupDistanceRight = rightIsShort ? 0 : rightNum - (2 * thicknessNum);
  
  // Calculate spacers for stations 2-4 (lip rolling)
  // Top = left setup + top side spacer + right setup
  // (Short sides contribute 0 setup since the side hasn't been bent yet)
  const topSpacer_2_4 = setupDistanceLeft + topSpacer_5_10 + setupDistanceRight;
  const bottomSpacer_2_4 = setupDistanceLeft + bottomSpacer_5_10 + setupDistanceRight;
  
  // Calculate lip-rolling gaps for stations 2-4
  // - Short side: the entire side folds here (treated as a lip-like fold).
  //   If a lip exists on that same side, the combined fold uses (side + lip) - 2t.
  // - Normal side: just the lip fold (current behavior), 0 if no lip.
  const calcFoldGap = (sideNum, lipNum, isShort) => {
    if (isShort && lipNum > 0) return (sideNum + lipNum) - (2 * thicknessNum);
    if (isShort) return sideNum - (2 * thicknessNum);
    if (lipNum > 0) return lipNum - (2 * thicknessNum);
    return 0;
  };
  const lipGapLeft = calcFoldGap(leftNum, leftLipNum, leftIsShort);
  const lipGapRight = calcFoldGap(rightNum, rightLipNum, rightIsShort);
  
  // Calculate Feeding Station spacers (Station 1)
  const feedingBottom = requiredSheet + 1;
  const feedingTop = feedingBottom - (2 * roundedThickness);
  
  return {
    // Stations 5-10 (sides)
    bottom_5_10: bottomSpacer_5_10,
    top_5_10: topSpacer_5_10,
    // Stations 2-4 (lips)
    bottom_2_4: bottomSpacer_2_4,
    top_2_4: topSpacer_2_4,
    // Station 1 (feeding)
    feedingBottom: feedingBottom,
    feedingTop: feedingTop,
    // Setup distance for sides
    setupDistanceLeft: setupDistanceLeft,
    setupDistanceRight: setupDistanceRight,
    // Lip gaps (now also covers short-side folds at stations 2-4)
    lipGapLeft: lipGapLeft,
    lipGapRight: lipGapRight,
    // Short side flags
    leftIsShort: leftIsShort,
    rightIsShort: rightIsShort,
    // Common
    roundedThickness: roundedThickness,
    actualThickness: thicknessNum,
    numberOfAngles: numberOfAngles
  };
};

// ===== MAIN CALCULATION =====

export const calculateSheetWidth = (base, leftSide, rightSide, leftLip, rightLip, thickness) => {
  const baseNum = parseFloat(base);
  const leftNum = parseFloat(leftSide);
  const rightNum = parseFloat(rightSide);
  const leftLipNum = parseFloat(leftLip) || 0;
  const rightLipNum = parseFloat(rightLip) || 0;
  const thicknessNum = parseFloat(thickness);
  
  // Calculate number of angles: 2 base angles + 1 for each lip > 0
  const numberOfAngles = 2 + (leftLipNum > 0 ? 1 : 0) + (rightLipNum > 0 ? 1 : 0);
  
  // Expansion = numberOfAngles * 2 * thickness
  const expansion = numberOfAngles * 2 * thicknessNum;
  
  // Calculate required sheet width
  const requiredSheet = baseNum + leftNum + rightNum + leftLipNum + rightLipNum - expansion;
  
  // Weight: substrate steel + Z100 zinc coating (100 g/m² both sides)
  // width(m) × 6m × (thickness_mm × 7.85 + 0.1) kg
  const weightFor6m = (requiredSheet / 1000) * 6 * (thicknessNum * 7.85 + 0.1);
  
  return {
    requiredSheet: requiredSheet.toFixed(1),
    expansion: expansion.toFixed(1),
    numberOfAngles: numberOfAngles,
    weightFor6m: weightFor6m.toFixed(2)
  };
};
